import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, ApiNetworkError } from '../api';
import { useAuth } from '../auth/AuthContext';
import { exchangeGoogleCredential } from '../auth/googleLogin';
import { AuthLayout } from '../components/AuthLayout';
import { GoogleSignInButton } from '../components/GoogleSignInButton';
import {
  trackGoogleSignInFailed,
  trackGoogleSignInStarted,
  trackGoogleSignInSucceeded,
  type GoogleSignInFailureReason,
  type GoogleSignInSource,
} from '../analytics';

type StatusMessage = { type: 'err' | 'info'; text: string };

const NETWORK_ERROR_MESSAGE = 'Google sign-in is temporarily unavailable. Please try again.';

function failureReason(error: unknown): GoogleSignInFailureReason {
  const status =
    error && typeof error === 'object' && 'status' in error
      ? (error as { status?: unknown }).status
      : undefined;

  if (error instanceof ApiNetworkError) return 'transient';
  if (status === 400 || status === 401) return 'invalid';
  if (status === 403) return 'inactive';
  if (status === 409) return 'collision';
  if (status === 500 || status === 502 || status === 503 || status === 504) return 'transient';
  return 'configuration';
}

function publicErrorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : NETWORK_ERROR_MESSAGE;
}

export function LoginPage() {
  const { authToken, user, loginSuccess, reauthReason, setReauthReason } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const activeExchangeRef = useRef(false);
  const exchangeAbortControllerRef = useRef<AbortController | null>(null);
  const wakeTimerRef = useRef<number | undefined>();
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from ?? '/';

  useEffect(() => {
    if (reauthReason) {
      setStatus({ type: 'err', text: reauthReason });
      setReauthReason(null);
    }
  }, [reauthReason, setReauthReason]);

  useEffect(() => () => {
    mountedRef.current = false;
    activeExchangeRef.current = false;
    exchangeAbortControllerRef.current?.abort();
    exchangeAbortControllerRef.current = null;
    if (wakeTimerRef.current !== undefined) {
      window.clearTimeout(wakeTimerRef.current);
      wakeTimerRef.current = undefined;
    }
  }, []);

  if (authToken && user) {
    return <Navigate to={from} replace />;
  }

  function clearWakeTimer() {
    if (wakeTimerRef.current === undefined) return;
    window.clearTimeout(wakeTimerRef.current);
    wakeTimerRef.current = undefined;
  }

  function handleCredential(credential: string, source: GoogleSignInSource) {
    if (activeExchangeRef.current) return;
    const abortController = new AbortController();
    activeExchangeRef.current = true;
    exchangeAbortControllerRef.current = abortController;
    setSigningIn(true);
    setStatus({ type: 'info', text: 'Signing you in…' });
    trackGoogleSignInStarted(source);
    wakeTimerRef.current = window.setTimeout(() => {
      if (mountedRef.current && activeExchangeRef.current) {
        setStatus({ type: 'info', text: 'Waking the service and signing you in…' });
      }
    }, 2_000);

    void exchangeGoogleCredential(credential, undefined, abortController.signal)
      .then((response) => {
        clearWakeTimer();
        if (!mountedRef.current) return;
        trackGoogleSignInSucceeded(source, response.accountCreated);
        loginSuccess(response.token, response.user);
        navigate(from, { replace: true });
      })
      .catch((error: unknown) => {
        clearWakeTimer();
        if (!mountedRef.current) return;
        trackGoogleSignInFailed(source, failureReason(error));
        setStatus({ type: 'err', text: publicErrorMessage(error) });
      })
      .finally(() => {
        if (exchangeAbortControllerRef.current === abortController) {
          exchangeAbortControllerRef.current = null;
        }
        activeExchangeRef.current = false;
        if (mountedRef.current) {
          setSigningIn(false);
        }
      });
  }

  return (
    <AuthLayout
      title="Selective Tests"
      subtitle="Use your Google account to sign in or create an account."
      variant="login"
    >
      {status ? (
        <div
          className={`alert alert-${status.type === 'err' ? 'danger' : 'info'}`}
          role={status.type === 'err' ? 'alert' : 'status'}
        >
          {status.text}
        </div>
      ) : null}
      <fieldset
        className="google-sign-in-control border-0 m-0 p-0"
        disabled={signingIn}
        aria-disabled={signingIn}
      >
        <GoogleSignInButton onCredential={handleCredential} />
      </fieldset>
    </AuthLayout>
  );
}
